import React, { useState, useEffect, useRef, useMemo } from 'react'
import Intro from "components/intro/Intro"
import Complete from "components/complete/Complete"
import MultiChoice from "components/challenges/multiChoice/MultiChoice"
import InputBox from "components/challenges/inputBox/InputBox"
import TextBox from "components/challenges/textBox/TextBox"
import MultiChoiceSplit from "components/challenges/multiChoiceSplit/MultiChoiceSplit"
import Match from "components/challenges/match/Match"

import Avatar from "components/Avatar"
import IntroModal from "components/IntroModal"
import PopupTooltip from "components/PopupTooltip"

import useTransitionStyles from './useTransitionStyles';

import { useNavigate, Link, useParams } from 'react-router-dom'



import FullLogo from "assets/images/logos/FullLogo.png"

import * as lessonParser from 'utils/lessonParser'
import ROUTES from 'utils/routes.js';

import { useUser, useDailyProgress, useUserStats } from 'utils/userContext';

import { supabase } from 'utils/supabaseClient';

import useStorageState from 'utils/storageState'

import { useAudio } from 'utils/audioContext'

const transitionStyles = {
    moveLeft: {
        transition: "opacity 300ms ease-in, transform 300ms ease-in",
        transform: "translateX(-15vw)",
        opacity: 0,
    },
    teleportRight: {
        transition: "",
        opacity: 0,
        transform: "translateX(18vw)",
    },
    moveToCenter: {
        transition: "opacity 300ms ease-out, transform 300ms ease-out",
        transform: "translateX(0)",
        opacity: 1,
    },
    teleportCenter: {
        transition: "opacity 300ms ease-out",
        opacity: 1,
        transform: "translateX(0)",
    },
    hidden: {
        opacity: 0,
        transform: "translateX(0)",
    }
};


function Lesson() {

    const { play } = useAudio();

    const { user, loading, updateUserData } = useUser();
    const { refreshProgress, incrementLessonDone } = useDailyProgress();
    const { logUserStat } = useUserStats();

    const navigatePage = useNavigate()


    const [transitionStyle, setTransitionStyle] = useTransitionStyles(transitionStyles, 'hidden');

    useEffect(() => {
        setTransitionStyle('moveToCenter');

        window.history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', function (event) {
            window.history.pushState(null, document.title, window.location.href);
        });
    }, []);

    const moveLeft = () => {
        setTransitionStyle('moveLeft');
    };

    const teleportRightAndCenter = () => {
        setTransitionStyle('teleportRight', 0);
        setTimeout(() => {
            requestAnimationFrame(() => {
                setTransitionStyle('moveToCenter');
            });
        }, 20);
    };

    const teleportCenter = () => {
        setTransitionStyle('teleportCenter', 0);
    };

    const [lessonTransition, setLessonTransition] = useState(false)


    const [lessonData, setLessonData] = useState([]);
    const [rawLessonJSON, setRawLessonJSON] = useState("");

    let { unitNum: urlUnitNum, conceptNum: urlConceptNum } = useParams()
    const currentLocation = useMemo(() => {
        const unit = Number(urlUnitNum) || 1;
        const concept = (urlConceptNum === "test" ? "test" : Number(urlConceptNum)) || 1;
        return { unit, concept };
    }, [urlUnitNum, urlConceptNum]);

    const LESSON_KEY = `concept-${currentLocation.unit}-${currentLocation.concept}`;

    const [savedData, setSavedData] = useStorageState(LESSON_KEY, {
        numChallengesDone: 0,
        currentChallenge: "intro", // "intro", "complete" or real challenge index
        challengesCompleted: [],
        skippedChallenges: [],
        currentChallengeKey: 1,
        numIncorrectAnswers: 0,
        introCompleted: false,
        linearCompleted: false,
        currentAnswered: false,
    },
        (prev) => {
            // console.log("Saved data for lesson changed:", LESSON_KEY, savedData);
            // Reset saved data when leaving the lesson
            // console.log("resetting saved data for lesson:", LESSON_KEY, currentLocation, prev);

            let resetData = {
                ...prev,
                numChallengesDone: 0,
                currentChallenge: "intro",
                challengesCompleted: [],
                skippedChallenges: [],
                currentChallengeKey: 1,
                numIncorrectAnswers: 0,
                introCompleted: false,
                currentAnswered: false,
            }
            return resetData;
        });

    function getNextChallengeIndex(savedData, lessonData, currentSkipped = false) {
        if (!lessonData || !lessonData.length) return 0;

        const units = lessonData;
        const currentUnit = units?.[currentLocation.unit - 1];
        const concepts = currentUnit?.concepts;
        const currentConcept = currentLocation.concept === "test" ? currentUnit["test"] : concepts?.[currentLocation.concept - 1];
        const challenges = currentConcept?.challenges;

        if (!challenges?.length) return 0;

        const currentIndex = savedData.currentChallenge;
        if (currentIndex === "intro" || currentIndex === "complete" || typeof currentIndex !== "number") return 0;

        const completed = new Set(savedData.challengesCompleted || []);
        const skippedSet = new Set(savedData.skippedChallenges || []);

        // If skipped, add to skippedChallenges if not already there
        if (currentSkipped && !completed.has(currentIndex) && !skippedSet.has(currentIndex)) {
            skippedSet.add(currentIndex);
            setSavedData(prev => ({
                ...prev,
                skippedChallenges: Array.from(skippedSet),
            }));
        }


        // If not skipped, mark current as completed and remove from skipped
        if (!currentSkipped && !completed.has(currentIndex)) {
            completed.add(currentIndex);
            skippedSet.delete(currentIndex);
            setSavedData(prev => ({
                ...prev,
                challengesCompleted: Array.from(completed),
                skippedChallenges: Array.from(skippedSet),
            }));
        }

        const totalChallenges = challenges.length;

        // First pass: continue forward to next uncompleted challenge
        for (let i = currentIndex + 1; i < totalChallenges; i++) {
            if (!completed.has(i)) return i;
        }
        for (let i = 0; i < currentIndex; i++) {
            if (!completed.has(i)) return i;
        }

        // Cleanup phase: go through skipped challenges
        for (const index of skippedSet) {
            if (!completed.has(index)) return index;
        }

        // All completed: random mode
        let randomIndex;
        do {
            randomIndex = Math.floor(Math.random() * totalChallenges);
        } while (randomIndex === currentIndex && totalChallenges > 1);

        return randomIndex;
    }

    function getLesson({ unit, concept }, lessonData) {
        if (!lessonData) return null;

        let fetchingChallenge = savedData.currentChallenge;

        if (fetchingChallenge === "intro") {
            function getIntroFallback() {
                return lessonData?.[unit - 1]?.concepts?.[concept - 1]?.intro ?? { type: "Intro", skip: true };
            }
            if (!savedData.introCompleted) {
                return getIntroFallback();
            }

            const concepts = lessonData?.[unit - 1]?.concepts;
            if (!concepts?.length) return getIntroFallback();

            const challenges = concept === "test" ? lessonData?.[unit - 1]?.test?.challenges : concepts?.[concept - 1]?.challenges;
            if (!challenges?.length) return getIntroFallback();

            const nextChallenge = getNextChallengeIndex(savedData, lessonData);
            setSavedData(prev => ({ ...prev, currentChallenge: nextChallenge, currentChallengeKey: prev.currentChallengeKey + 1 || 2, currentAnswered: false }));
            fetchingChallenge = nextChallenge;
        }
        if (fetchingChallenge === "complete") return { type: "Complete", skip: true };
        return concept === "test" ? lessonData?.[unit - 1]?.test?.challenges?.[fetchingChallenge] : lessonData?.[unit - 1]?.concepts?.[concept - 1]?.challenges?.[fetchingChallenge];
    }

    function getConcept({ unit, concept }, lessonData) {
        return concept === "test" ? lessonData?.[unit - 1]?.unit + " Test" : lessonData?.[unit - 1]?.concepts?.[concept - 1]?.concept || `Unit ${unit}:${concept}`;
    }

    function getIntroContent({ unit, concept }, lessonData) {
        if (!lessonData || concept === "test") return null;
        return lessonData?.[unit - 1]?.concepts?.[concept - 1]?.intro?.data?.content || null;
    }

    const currentChallengeData = useMemo(() => {

        return getLesson(currentLocation, lessonData);
    }, [currentLocation, lessonData, savedData.currentChallenge]);

    const [questionVariables, setQuestionVariables] = useState([]);

    const [questionReady, setQuestionReady] = useState(null);

    const [questionTitle, setQuestionTitle] = useState(<span></span>);


    const [questionStatus, setQuestionStatus] = useState({
        guessed: false,
        answer: null,
        message: null,
    });

    const [challengeStartTime, setChallengeStartTime] = useState(null);
    const [helpUsed, setHelpUsed] = useState(false);
    const [introButtonDisabled, setIntroButtonDisabled] = useState({ disabled: false, progress: 1 });
    const [isIntroModalOpen, setIsIntroModalOpen] = useState(false);


    const leaveFnRef = useRef(null);
    const answerCheckRef = useRef(null);
    const introTooltipRef = useRef(null);

    const challengeComponents = {
        Intro,
        Complete,
        MultiChoice,
        InputBox,
        TextBox,
        MultiChoiceSplit,
        Match,
    };

    useEffect(() => {
        if (!currentChallengeData || !questionStatus) return;

        if (loading) return;
        // console.log("Current lesson data changed:", currentLessonData, questionStatus);
        if (!questionStatus.guessed && savedData.currentAnswered) {
            if (!user?.is_admin) {
                setQuestionStatus({ guessed: true, answer: savedData.currentAnswered === "correct", message: null });
            }
        }
    }, [savedData, currentChallengeData, loading]);


    const minNumChallenges = 6;
    const maxConceptRepeats = 3; // How many times a concept must be repeated before moving to the next one

    function getNextLesson({ unit, concept }, lessonData, skipped = false, incorrect = false) {
        const unitIndex = unit - 1;
        const conceptIndex = concept - 1;

        const concepts = lessonData?.[unitIndex]?.concepts;
        if (!concepts) return null;

        let nextConcept = concept;
        let nextUnit = unit;

        const challenges = concept === "test" ? lessonData?.[unitIndex]?.test?.challenges : concepts?.[conceptIndex]?.challenges;
        if (!challenges) {
            if (concept === "test") {
                nextConcept = 1;
                nextUnit++;

                if (nextUnit > lessonData.length) {
                    return null;
                }
                return { unit: nextUnit, concept: nextConcept, conceptProgress: 1 };
            } else {
                return null;
            }
        };



        let currentConceptProgress = user?.currentConceptProgress ?? 1;

        if (savedData.currentChallenge === "intro") {
            let nextChallenge = getNextChallengeIndex(savedData, lessonData, skipped);
            setSavedData(prev => ({ ...prev, currentChallenge: nextChallenge, currentChallengeKey: prev.currentChallengeKey + 1 || 2, currentAnswered: false }));
            return { unit, concept, conceptProgress: currentConceptProgress };
        }

        if (savedData.currentChallenge === "complete") {
            setSavedData(prev => ({ ...prev, introCompleted: true, currentAnswered: false }));

            let shouldRepeat = savedData.numIncorrectAnswers >= Math.max(Math.floor(((1 / 4) * savedData.numChallengesDone)), 1);
            console.log("Current concept progress:", currentConceptProgress, "Max concept repeats:", maxConceptRepeats, "Num challenges done:", savedData.numChallengesDone, "Num incorrect answers:", savedData.numIncorrectAnswers, "Should repeat:", shouldRepeat);
            if (currentConceptProgress < maxConceptRepeats && shouldRepeat) {
                console.log("Repeating concept:", currentConceptProgress, "of unit:", unit, "concept:", concept);
                // Concept needs to be repeated - increment progress but stay on same concept
                // This will be persisted later by updateProgressIfNeeded
                return { unit, concept, conceptProgress: currentConceptProgress + 1 };
            }
            console.log("Moving to next concept:", currentConceptProgress, "of unit:", unit, "concept:", concept);
            if (typeof nextConcept === "number") {
                nextConcept++;
            }

            if (nextConcept === "test") {
                // Check if test exists before moving to next unit
                const testExists = lessonData?.[unitIndex]?.test;
                if (testExists) {
                    nextConcept = 1;
                    nextUnit++;

                    if (nextUnit > lessonData.length) {
                        return null;
                    }
                } else {
                    // No test exists, move to next unit directly
                    nextConcept = 1;
                    nextUnit++;

                    if (nextUnit > lessonData.length) {
                        return null;
                    }
                }
            } else {
                if (nextConcept > concepts.length) {
                    // Check if test exists before setting nextConcept to "test"
                    const testExists = lessonData?.[unitIndex]?.test;
                    if (testExists) {
                        nextConcept = "test";
                    } else {
                        // No test exists, move to next unit
                        nextConcept = 1;
                        nextUnit++;

                        if (nextUnit > lessonData.length) {
                            return null;
                        }
                    }
                }
            }


            return { unit: nextUnit, concept: nextConcept, conceptProgress: 1 };
        }

        // Show complete screen when ALL challenges are done (not one early)
        if (!skipped && (savedData.numChallengesDone >= Math.max(minNumChallenges, challenges.length))) {
            setSavedData(prev => ({ ...prev, currentChallenge: "complete", currentAnswered: false }));
            return { unit, concept, conceptProgress: currentConceptProgress };
        }

        console.log("NEXT", incorrect)
        let nextChallenge = getNextChallengeIndex(savedData, lessonData, skipped);
        setSavedData(prev => ({ ...prev, numChallengesDone: skipped ? prev.numChallengesDone : prev.numChallengesDone + 1, currentChallenge: nextChallenge, currentChallengeKey: prev.currentChallengeKey + 1 || 2, currentAnswered: false, numIncorrectAnswers: incorrect ? (prev.numIncorrectAnswers ?? 0) + 1 : (prev.numIncorrectAnswers ?? 0) }));
        return { unit: nextUnit, concept: nextConcept, conceptProgress: currentConceptProgress };
    }

    const rankConcept = c => (c === 'test' ? Number.MAX_SAFE_INTEGER : Number(c) || 1);


    function isConceptAfter(curr = {}, next = {}) {
        const currUnit = curr.unit ?? 1;
        const nextUnit = next.unit ?? 1;

        if (currUnit !== nextUnit) return currUnit < nextUnit;

        const currConcept = rankConcept(curr.concept);
        const nextConcept = rankConcept(next.concept);

        if (currConcept !== nextConcept) return currConcept < nextConcept;

        const currProg = curr.conceptProgress ?? 1;
        const nextProg = next.conceptProgress ?? 1;

        return currProg < nextProg;
    }

    function shouldUpdateUserProgress(user, next) {
        if (!user) return false;
        const nextUnit = next.unit ?? 1;
        const nextConcept = next.concept ?? 1;
        const nextConceptProgress = next.conceptProgress ?? 1;

        const currUnit = user.currentUnit ?? 1;
        const currConcept = user.currentConcept === 999 ? "test" : user.currentConcept ?? 1;
        const currentConceptProgress = user.currentConceptProgress ?? 1;

        return isConceptAfter({ unit: currUnit, concept: currConcept, conceptProgress: currentConceptProgress },
            { unit: nextUnit, concept: nextConcept, conceptProgress: nextConceptProgress });
    }

    async function updateProgressIfNeeded(user, next, updateUserData, incrementLessonDone) {
        if (!shouldUpdateUserProgress(user, next)) return;

        if (user?.isGuest) {
            if (next.unit === 1 && next.concept === 1) {
                await updateUserData({
                    currentConceptProgress: next.conceptProgress
                });
            }

            navigatePage(ROUTES.LESSONS);
            return;
        }

        await updateUserData({
            currentUnit: next.unit,
            currentConcept: next.concept === "test" ? 999 : Number(next.concept),
            currentConceptProgress: next.conceptProgress
        });

        if (!user.is_admin) {
            navigatePage(ROUTES.LESSONS);
        }

        if (user?.id) {
            await incrementLessonDone(user.id);
        }
    }

    function startTransition(skipped = false, incorrect = false) {
        setLessonTransition(true)
        moveLeft()

        // Capture whether we're on complete screen BEFORE any state changes
        const wasOnCompleteScreen = savedData.currentChallenge === "complete";

        setTimeout(async () => {
            setLessonTransition(false);

            const next = getNextLesson(currentLocation, lessonData, skipped, incorrect);
            // console.log("Next lesson:", next);
            if (!next) {
                console.error("Next lesson not found, redirecting...");
                navigatePage(ROUTES.LESSONS);
                return;
            }

            // Log user stats for completed challenge
            if (challengeStartTime && typeof savedData.currentChallenge === 'number') {
                const timeSpentMs = Date.now() - challengeStartTime;
                const isCorrect = !skipped && !incorrect;

                try {
                    await logUserStat({
                        unitNum: currentLocation.unit,
                        conceptNum: currentLocation.concept === "test" ? 999 : Number(currentLocation.concept),
                        lessonNum: savedData.currentChallenge, // 0-indexed challenge index
                        timeSpentMs: timeSpentMs,
                        isCorrect: isCorrect,
                        generatedHelp: helpUsed
                    });


                } catch (error) {
                    console.error('Failed to log user stat:', error);
                }
            }

            await updateProgressIfNeeded(user, next, updateUserData, incrementLessonDone);

            // If we were on the complete screen, reset the saved data to start fresh next time
            if (wasOnCompleteScreen) {
                console.log("Resetting saved data after completing concept");
                const resetData = {
                    numChallengesDone: 0,
                    currentChallenge: "intro",
                    challengesCompleted: [],
                    skippedChallenges: [],
                    currentChallengeKey: 1,
                    numIncorrectAnswers: 0,
                    introCompleted: true, // Keep intro completed so it skips to challenges
                    linearCompleted: false,
                    currentAnswered: false,
                };

                // Update both state and localStorage immediately
                setSavedData(prev => resetData);
                const storageKey = `kodeo.concept-${currentLocation.unit}-${currentLocation.concept}`;
                localStorage.setItem(storageKey, JSON.stringify(resetData));
            }

            if (isConceptAfter(
                { unit: currentLocation.unit ?? 1, concept: currentLocation.concept ?? 1, conceptProgress: user.currentConceptProgress ?? 1 },
                { unit: next.unit, concept: next.concept, conceptProgress: next.conceptProgress ?? 1 }
            )) {
                navigatePage(
                    ROUTES.LESSONS
                );
            } else {
                if (savedData.currentChallenge === "complete") {
                    teleportCenter();
                } else {
                    teleportRightAndCenter();
                }
            }
        }, 350)
    }

    function isConceptUnlocked(user, current) {
        if (!user || user.is_admin) return true;

        if (user.isGuest) {
            return current.unit === 1 && current.concept === 1;
        }

        const userUnit = user.currentUnit ?? 1;
        const userConcept = user.currentConcept === 999 ? "test" : user.currentConcept ?? 1;

        const currentUnit = current.unit;
        const currentConcept = current.concept;

        return !isConceptAfter(
            { unit: userUnit, concept: userConcept, conceptProgress: 1 },
            { unit: currentUnit, concept: currentConcept, conceptProgress: 1 }
        );
    }

    useEffect(() => {
        let cancelled = false;

        (async () => {
            // const { data, error } = await supabase.storage.from('units').download('lessons.json');
            // if (error) {
            //     console.error("Error fetching lesson data:", error);
            //     return;
            // }
            // use local file for development
            let data
            try {
                data = await fetch('/lessons.json');
                if (!data.ok) {
                    console.error("Error fetching lesson data:", data.statusText);
                    return;
                }
            } catch (error) {
                console.error("Error fetching lesson data:", error);
                return;
            }

            const jsonText = await data.text();
            const units = JSON.parse(jsonText);
            if (!cancelled) {
                setLessonData(units);
                setRawLessonJSON(jsonText);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [setLessonData]);

    useEffect(() => {
        if (!lessonData?.length || loading) return;

        if (user?.isGuest && (currentLocation.unit !== 1 || currentLocation.concept !== 1)) {
            navigatePage(ROUTES.LESSON.replace(':unitNum', 1).replace(':conceptNum', 1));
            return;
        }

        if (!isConceptUnlocked(user, currentLocation)) {
            console.warn("Concept not unlocked for user", user, currentLocation);
            navigatePage(
                ROUTES.LESSON
                    .replace(':unitNum', (user.currentUnit ?? 1))
                    .replace(':conceptNum', (user.currentConcept === 999 ? "test" : user.currentConcept ?? 1))
                // .replace(':lessonNum', 1)
            );
            return;
        }

        const isTestConcept = currentLocation.concept === "test";

        if (
            savedData.currentChallenge !== "intro" &&
            savedData.currentChallenge !== "complete" && (

                // ‼️ Always validate the unit index
                currentLocation.unit < 1 ||
                currentLocation.unit > lessonData.length ||

                // ✅ Validate the concept index only when it’s NOT "test"
                (!isTestConcept && (
                    currentLocation.concept < 1 ||
                    currentLocation.concept >
                    lessonData[currentLocation.unit - 1]?.concepts?.length
                ))
            )
        ) {
            console.warn("Lesson navigation out of bounds", currentLocation);
            navigatePage(ROUTES.LESSONS);
        }
    }, [loading, user, lessonData, currentLocation]);

    useEffect(() => {
        if (!lessonData?.length || !currentLocation) return;

        if (savedData.currentChallenge === "complete") {
            setQuestionTitle(getConcept(currentLocation, lessonData));
            setQuestionVariables([]);
            setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}-${savedData.currentChallengeKey}`);
            return;
        }

        let testExists = lessonData[currentLocation.unit - 1]?.test;
        if (currentLocation.concept === "test" && !testExists) {
            const next = getNextLesson(currentLocation, lessonData);
            if (!next) {
                console.error("Next lesson not found, redirecting to lessons page");
                navigatePage(ROUTES.LESSONS);
                return;
            }
            updateProgressIfNeeded(user, next, updateUserData, incrementLessonDone).then(() => {
                navigatePage(
                    ROUTES.LESSON
                        .replace(":unitNum", next.unit)
                        .replace(":conceptNum", next.concept)
                );
            }).catch(err => {
                console.error("Error updating progress:", err);
                navigatePage(ROUTES.LESSONS); // Fallback navigation
            });
            return;
        }

        let introExists = lessonData[currentLocation.unit - 1]?.concepts?.[currentLocation.concept - 1]?.intro;
        if (savedData.currentChallenge === "intro" && !introExists) {
            const next = getNextLesson(currentLocation, lessonData);
            if (!next) {
                console.error("Next lesson not found, redirecting to lessons page");
                navigatePage(ROUTES.LESSONS);
                return;
            }
            navigatePage(
                ROUTES.LESSON
                    .replace(":unitNum", next.unit)
                    .replace(":conceptNum", next.concept)
            );
            return;
        }



        if (!currentChallengeData) return;

        setQuestionReady("");

        if (currentChallengeData.type === "Intro") {
            // Intro lesson, no question
            setQuestionTitle(<span className='text-3xl font-bold'>Lesson Intro</span>);
            setQuestionVariables([]);
            setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}-${savedData.currentChallengeKey}`);
            return;
        }

        // ----- compute everything in local vars first -----
        const index = Math.floor(Math.random() * currentChallengeData.question.length);

        const rawVars = currentChallengeData.variables || [];
        const parsedVars = rawVars.length
            ? lessonParser.parseValueList(rawVars, true)
            : [];

        const questionHTML = lessonParser
            .replaceVariablesInText(currentChallengeData.question[index], parsedVars)
            .replace(/\*\*(.*?)\*\*/g, '<span class="questionTitleBolded">$1</span>')
            .replace(/\*(.*?)\*/g, '<span class="questionTitleUnderscore">$1</span>')
            .replace(/\`(.*?)\`/g, '<span class="questionTitleCode">$1</span>');

        // ----- now push to React in one batch -----
        setQuestionVariables(parsedVars);
        setQuestionTitle(<span dangerouslySetInnerHTML={{ __html: questionHTML }} />);
        setQuestionReady(`${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}-${savedData.currentChallengeKey}`);

        // Start timing for this challenge
        setChallengeStartTime(Date.now());
        setHelpUsed(false); // Reset help usage for new challenge
    }, [currentChallengeData, savedData.currentChallengeKey]);



    const PING_INTERVAL_MS = 10000;
    const pingIntervalRef = useRef(null);
    const isPingingRef = useRef(false);
    const isMountedRef = useRef(true);

    async function startTimeTracking(userId) {
        if (isPingingRef.current) return; // prevent multiple intervals
        isPingingRef.current = true;

        pingIntervalRef.current = setInterval(async () => {
            if (!isMountedRef.current) return; // Stop if unmounted
            if (document.visibilityState !== "visible") return;

            const now = new Date();
            const todayStr = now.toLocaleDateString('sv-SE');

            // Fetch current row to check last ping
            const { data, error } = await supabase
                .from("user_daily_progress")
                .select("user_id, last_ping_at, minutes_worked")
                .eq("user_id", userId)
                .eq("date", todayStr)
                .limit(1)
                .maybeSingle();

            if (!isMountedRef.current) return; // Check again after async operation

            // console.log("Ping fetch data:", data, "error:", error);

            if (error && error.code !== "PGRST116") {
                console.error("Ping fetch error:", error);
                return;
            }

            const lastPing = data?.last_ping_at ? new Date(data.last_ping_at) : null;
            const secondsSinceLast = lastPing ? (now - lastPing) / 1000 : null;

            let minutesToAdd = 0;
            const intervalSec = PING_INTERVAL_MS / 1000;

            if (!lastPing || secondsSinceLast > intervalSec * 2) {
                // Missed too many pings or just started — credit one interval
                minutesToAdd = intervalSec / 60;
            } else if (secondsSinceLast >= intervalSec * 0.8 && secondsSinceLast <= intervalSec * 2) {
                // Valid ping — credit actual time
                minutesToAdd = secondsSinceLast / 60;
            } else {
                // Too soon or overlapping — ignore
                return;
            }

            const newMinutesWorked = (data?.minutes_worked ?? 0) + minutesToAdd;

            if (newMinutesWorked > user?.timeGoal && data?.minutes_worked < user?.timeGoal) {
                console.log("Daily progress updated: Time goal reached!");
                window.finishTimeGoal?.();
            } else {
                console.log("Daily progress updated: Added", minutesToAdd, "minutes. Total now:", newMinutesWorked);
            }

            // UPSERT row
            const { error: upsertError } = await supabase
                .from("user_daily_progress")
                .upsert({
                    user_id: userId,
                    date: todayStr,
                    minutes_worked: newMinutesWorked,
                    last_ping_at: now.toISOString(),
                }, { onConflict: ['user_id', 'date'] });


            if (!isMountedRef.current) return; // Check again after async operation

            if (upsertError) console.error("Ping upsert error:", upsertError);

        }, PING_INTERVAL_MS);
    }

    function stopTimeTracking() {
        clearInterval(pingIntervalRef.current);
        isPingingRef.current = false;
    }

    useEffect(() => {
        if (loading || !user?.id) return;

        isMountedRef.current = true;
        // console.log("Starting time tracking for user:", user?.id);
        startTimeTracking(user.id);

        return () => {
            isMountedRef.current = false;
            // console.log("Stopping time tracking for user:", user?.id);
            stopTimeTracking();
        };
    }, [loading, user?.id]);



    useEffect(() => {
        let keybindWatcher = (e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.altKey) {
                let controlKey = e.ctrlKey || e.metaKey;
                if (!controlKey && !questionStatus.guessed && currentChallengeData.type !== "Intro" && currentChallengeData.type !== "Complete") {
                    return
                }
                e.preventDefault();
                handleCheck().catch(err => {
                    console.error("Error in handleCheck:", err);
                });
            }
        };
        document.addEventListener("keydown", keybindWatcher);
        return () => {
            document.removeEventListener("keydown", keybindWatcher);
        };
    }, [questionStatus, currentChallengeData, lessonTransition, answerCheckRef, leaveFnRef]);

    async function handleCheck(skip = false) {
        try {
            if (questionStatus.skipped) {
                skip = true;
            }

            if (lessonTransition) return
            if (questionStatus.guessed || currentChallengeData.type === "Intro" || currentChallengeData.type === "Complete") {
                if (currentChallengeData.type === "Intro") {
                    if (answerCheckRef.current) {
                        const result = await answerCheckRef.current?.();
                        if (!result) return;
                    } else {
                        return
                    }
                }

                startTransition(skip || questionStatus.answer === false, !skip && questionStatus.answer === false);
                setQuestionStatus({ guessed: false, answer: null, message: null });
                if (leaveFnRef.current) {
                    leaveFnRef.current();
                }
                // console.log(lessonData)
            } else if (answerCheckRef.current) {
                const result = await answerCheckRef.current?.(skip);
                if (!result) return; // or handle this case however you want

                const { isCorrect, message } = result;
                if (isCorrect !== null) {
                    if (!isCorrect) {
                        play('incorrect.mp3');
                    } else {
                        play('correct.mp3');
                    }
                    // console.log("Answer check result:", isCorrect, message);
                    setSavedData(prev => ({ ...prev, currentAnswered: isCorrect ? "correct" : "incorrect" }));
                    setQuestionStatus({ guessed: true, answer: isCorrect, message: message || null, skipped: skip });
                }
            }
        } catch (err) {
            console.error("Error in handleCheck:", err);
            // Optionally show error to user
            setQuestionStatus({ guessed: true, answer: false, message: "An error occurred. Please try again." });
        }
    }
    function handleSkip() {
        if (lessonTransition) return
        startTransition(true);
        if (leaveFnRef.current) {
            leaveFnRef.current();
        }

        play('skip.mp3');

        setQuestionStatus({ guessed: true, answer: null, message: null });
    }


    function getChallengeComponent(questionData, questionText) {
        const Component = challengeComponents[questionData?.type];
        leaveFnRef.current = null; // Reset leave function for new question
        if (Component) {
            const componentKey = currentLocation.unit + "-" + currentLocation.concept + "-" + savedData.currentChallenge + "-" + savedData.currentChallengeKey;
            const commonProps = {
                questionData: questionData,
                questionVariables: questionVariables,
                questionText: questionText,
                questionStatus: questionStatus,
                onAnswerCheck: (checkFn) => (answerCheckRef.current = checkFn),
                onLeave: (leaveFn) => { leaveFnRef.current = leaveFn },
                onHelpUsed: () => setHelpUsed(true),
                manualCheck: () => handleCheck(),
                tag: questionData?.tag // Pass tag to challenge components
            };

            // Add extra props for Intro component
            if (questionData?.type === "Intro") {
                return (
                    <Component
                        key={componentKey}
                        {...commonProps}
                        currentConceptProgress={user?.currentConceptProgress ?? 1}
                        setIntroButtonDisabled={setIntroButtonDisabled}
                    />
                );
            }

            return <Component key={componentKey} {...commonProps} />;
        } else {
            return <div className='w-max text-2xl'>Challenge type "{questionData?.type}" not recognized. Please contact help if this continues.</div>;
        }
    }

    function randomStatusWord(isCorrect, randomKey) {
        const words = isCorrect
            ? ["Great!", "Awesome!", "Well done!", "Fantastic!", "Excellent!"]
            : ["Oops!", "Try again!", "Not quite.", "Almost there!", "Keep trying!"];

        // Use a hash of the lesson ID or similar as the seed
        const seedStr = randomKey || "default";
        let hash = 0;
        for (let i = 0; i < seedStr.length; i++) {
            hash = (hash << 5) - hash + seedStr.charCodeAt(i);
            hash |= 0; // Convert to 32bit integer
        }

        const index = Math.abs(hash) % words.length;
        return words[index];
    }

    function findChallengeLineNumber() {
        if (!rawLessonJSON || !currentChallengeData || typeof savedData.currentChallenge !== 'number') return null;

        try {
            const unitIndex = currentLocation.unit - 1;
            const conceptIndex = currentLocation.concept === "test" ? null : currentLocation.concept - 1;
            const challengeIndex = savedData.currentChallenge;

            // Count how many challenge objects come before our target
            let challengesToSkip = 0;

            for (let u = 0; u < lessonData.length; u++) {
                const unit = lessonData[u];

                // Process concepts
                if (unit.concepts) {
                    for (let c = 0; c < unit.concepts.length; c++) {
                        const concept = unit.concepts[c];
                        if (concept.challenges) {
                            for (let ch = 0; ch < concept.challenges.length; ch++) {
                                // Is this our target?
                                if (u === unitIndex && currentLocation.concept !== "test" && c === conceptIndex && ch === challengeIndex) {
                                    // Found it! Now find the Nth challenge object in the JSON
                                    return findNthChallengeObject(rawLessonJSON, challengesToSkip);
                                }
                                challengesToSkip++;
                            }
                        }
                    }
                }

                // Process test challenges
                if (unit.test?.challenges) {
                    for (let ch = 0; ch < unit.test.challenges.length; ch++) {
                        // Is this our target?
                        if (u === unitIndex && currentLocation.concept === "test" && ch === challengeIndex) {
                            // Found it! Now find the Nth challenge object in the JSON
                            return findNthChallengeObject(rawLessonJSON, challengesToSkip);
                        }
                        challengesToSkip++;
                    }
                }
            }

            return null;
        } catch (error) {
            console.error("Error finding line number:", error);
            return null;
        }
    }

    function findNthChallengeObject(jsonText, n) {
        // Parse through JSON and find the nth occurrence of a challenge object
        // A challenge object is inside a "challenges" array

        let inString = false;
        let escapeNext = false;
        let depth = 0;
        let currentLine = 1;
        let challengeCount = 0;
        let inChallengesArray = false;
        let challengesArrayDepth = -1;
        let objectDepthInArray = -1;

        for (let i = 0; i < jsonText.length; i++) {
            const char = jsonText[i];

            if (char === '\n') currentLine++;

            // Handle strings
            if (escapeNext) {
                escapeNext = false;
                continue;
            }
            if (char === '\\' && inString) {
                escapeNext = true;
                continue;
            }
            if (char === '"') {
                inString = !inString;
                continue;
            }

            if (inString) continue;

            // Track depth
            if (char === '{' || char === '[') {
                depth++;

                // Check if we're entering a "challenges" array
                if (char === '[') {
                    const lookback = jsonText.substring(Math.max(0, i - 20), i);
                    if (/"challenges"\s*:\s*$/.test(lookback)) {
                        inChallengesArray = true;
                        challengesArrayDepth = depth;
                    }
                }

                // Count direct child objects of challenges array
                // They appear at depth = challengesArrayDepth + 1
                if (char === '{' && inChallengesArray && depth === challengesArrayDepth + 1) {
                    if (challengeCount === n) {
                        return currentLine;
                    }
                    challengeCount++;
                }
            }

            if (char === '}' || char === ']') {
                // Exit challenges array when we close it
                if (char === ']' && inChallengesArray && depth === challengesArrayDepth) {
                    inChallengesArray = false;
                    challengesArrayDepth = -1;
                }
                depth--;
            }
        }

        return null;
    }

    const currentLineNumber = useMemo(() => {
        return findChallengeLineNumber();
    }, [rawLessonJSON, currentChallengeData, savedData.currentChallenge]);


    return (
        <div className='Lesson text-text grid-rows-split-small break-small:grid-rows-split-main h-full bg-background text-center grid  overflow-hidden'>
            <IntroModal
                isOpen={isIntroModalOpen}
                onClose={() => setIsIntroModalOpen(false)}
                content={getIntroContent(currentLocation, lessonData)}
            />

            <div className="z-10 fixed top-0 left-0 right-0 grid grid-cols-[repeat(3,1fr)] px-2 py-2 pointer-events-none">
                <div className='flex justify-start items-start flex-col gap-1'>
                    <Link to={ROUTES.LESSONS} className='cursor-pointer pointer-events-auto w-full font-bold text-left text-[1rem] break-small:text-[1rem]'>
                        {getConcept(currentLocation, lessonData)}
                    </Link>
                    {currentChallengeData?.type !== "Intro" && currentChallengeData?.type !== "Complete" && currentLocation.concept !== "test" && (
                        <div className="relative">
                            <button
                                onClick={() => {
                                    // Close the tooltip when clicking the intro button
                                    if (introTooltipRef.current) {
                                        introTooltipRef.current.handleInteraction();
                                    }
                                    setIsIntroModalOpen(prev => !prev);
                                }}
                                className='pointer-events-auto flex items-center gap-1 text-sm text-text opacity-60 hover:opacity-100 transition-opacity group'
                                aria-label="View lesson introduction"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className='text-xs'>Intro</span>
                            </button>
                            <PopupTooltip
                                ref={introTooltipRef}
                                text="You can always access the intro for help here!"
                                open={
                                    currentChallengeData?.type !== "Intro" &&
                                    currentChallengeData?.type !== "Complete" &&
                                    user?.currentConceptProgress === 1
                                }
                                storageKey="intro-button-help-global"
                                autoMarkSeenAfter={15000}
                                autoClose={true}
                                top="50%"
                                right="calc(100% + 16px)"
                                width="220px"
                            />
                        </div>
                    )}
                </div>
                <div className='flex justify-center items-start pt-1'>
                    <img className='w-28 text-center _noDrag bg-background rounded-xl p-2 pt-0' src={FullLogo} />
                </div>
                <div className='flex justify-end items-center'>
                    {user?.isGuest ? (
                        <div className='flex items-center gap-4'>You are in guest mode. <Link to={ROUTES.SIGNUP} className="bg-background-light text-white font-bold px-4 py-2 rounded-2xl shadow-md hover:bg-background transition-all duration-500 text-sm border-2 border-primary pointer-events-auto">CREATE AN ACCOUNT</Link></div>
                    ) : (
                        <Avatar ></Avatar>
                    )}
                </div>
            </div>

            {!loading && user.is_admin && <div className="z-20 absolute flex flex-col items-end top-24 right-4 gap-1">
                <div className="flex items-center gap-2">
                    <h1>1-{currentLocation.concept === "test" ?
                        lessonData[currentLocation.unit - 1]?.test?.challenges.length :
                        lessonData[currentLocation.unit - 1]?.concepts[currentLocation.concept - 1]?.challenges.length}</h1>
                    <input type="number" value={savedData.currentChallenge + 1} onChange={(e) => {
                        const maxChallenges = currentLocation.concept === "test" ?
                            lessonData[currentLocation.unit - 1]?.test?.challenges.length :
                            lessonData[currentLocation.unit - 1]?.concepts[currentLocation.concept - 1]?.challenges.length;
                        if (e.target.value > 0 && e.target.value < 1 + maxChallenges) return setSavedData(prev => ({ ...prev, currentChallenge: Number(e.target.value) - 1 }))
                    }} className='outline-none w-12 p-1 text-center bg-transparent rounded-lg border-2 border-border focus:border-border-light' />
                    <button className='appearance-auto' onClick={() => setSavedData(prev => ({ ...prev, currentChallengeKey: prev.currentChallengeKey + 1 }))}>↻</button>
                </div>
                {currentLineNumber && (
                    <button
                        className='text-xs opacity-60 hover:opacity-100 transition-opacity cursor-pointer'
                        title="Click to copy line number"
                        onClick={() => {
                            navigator.clipboard.writeText(currentLineNumber.toString());
                        }}
                    >
                        L{currentLineNumber}
                    </button>
                )}
            </div>}


            <div className="mt-6 Question max-h-full overflow-y-auto w-full flex justify-center items-center" style={transitionStyle}>
                {questionReady === `${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}-${savedData.currentChallengeKey}` && getChallengeComponent(currentChallengeData, questionTitle)}
            </div>
            <div className="Info w-full font-bold h-[160px] py-2 break-small:py-0 px-4 break-small:h-[110px] border-t-2 bg-background border-border flex items-center justify-center">
                <div className='gap-4 flex flex-col break-small:flex-row  items-center justify-center break-small:justify-between w-full break-small:w-[850px] break-big:w-[900px] h-full'>
                    {currentChallengeData?.type === "Intro" || currentChallengeData?.type === "Complete" ?
                        <div></div> :
                        questionStatus["guessed"] ? <div className='flex flex-row items-center gap-2'>
                            {questionStatus["answer"] ? <YesIcon /> : <NoIcon />}
                            <h1 className='text-3xl font-black' style={{ color: questionStatus["answer"] ? "var(--accent)" : "var(--warning)" }}>{questionStatus["answer"] ? randomStatusWord(true, `${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}`) : randomStatusWord(false, `${currentLocation.unit}-${currentLocation.concept}-${savedData.currentChallenge}`)}</h1>
                            {/* <div className='flex flex-col items-start'> 
                                 <h2 className='text-xl font-medium  ml-2 text-warning'>{!questionStatus["answer"] && questionStatus["message"] !== null ? `Answer: ${questionStatus["message"]}` : null}</h2> 
                             </div> */}
                        </div> : <>
                            <button className='border-[3px] w-full break-small:w-48 border-border px-2 py-3 rounded-3xl text-border text-2xl' onClick={() => handleCheck(true).catch(err => console.error("Error in handleCheck:", err))}>SKIP</button>
                        </>}
                    <button
                        style={{
                            opacity: currentChallengeData?.type === "Match" && !questionStatus.guessed ? 0.5 : 1
                        }}
                        className={`${questionStatus["guessed"] ? questionStatus["answer"] ? "bg-accent" : "bg-warning" : currentChallengeData?.type === "Intro" && introButtonDisabled.disabled ? "bg-primary-dark" : "bg-primary"} ${questionStatus["guessed"] ? questionStatus["answer"] ? "shadow-accent-dark" : "shadow-warning-dark" : currentChallengeData?.type === "Intro" && introButtonDisabled.disabled ? "shadow-primary-darker" : "shadow-primary-dark"} w-full break-small:w-48 text-center text-background  px-2 py-3 rounded-3xl text-3xl shadow-b-md ${currentChallengeData?.type === "Intro" && introButtonDisabled.disabled ? "" : "active:shadow-none active:translate-y-1"}`}
                        onClick={(e) => {
                            if (currentChallengeData?.type === "Intro" && introButtonDisabled.disabled) {
                                const btn = e.currentTarget;
                                btn.classList.remove('intro-button-shake');
                                void btn.offsetWidth; // Force reflow
                                btn.classList.add('intro-button-shake');
                                setTimeout(() => {
                                    btn.classList.remove('intro-button-shake');
                                }, 500);
                                return;
                            }
                            handleCheck().catch(err => console.error("Error in handleCheck:", err));
                        }}
                    >
                        {currentChallengeData?.type === "Intro" || currentChallengeData?.type === "Complete" || currentChallengeData?.type === "Match" ? "NEXT" : !questionStatus["guessed"] ? "CHECK" : "NEXT"}
                    </button>
                </div>
            </div>

        </div>
    )
}


const YesIcon = () => (
    <svg width="35" height="35" viewBox="0 0 64 64" style={{ display: 'inline-block', verticalAlign: 'middle', marginBottom: '0px', marginRight: "2px" }} xmlns="http://www.w3.org/2000/svg">
        <path d="M28.2666 36.7999L22.5333 31.0666C22.0444 30.5777 21.4221 30.3333 20.6666 30.3333C19.911 30.3333 19.2888 30.5777 18.7999 31.0666C18.311 31.5555 18.0666 32.1777 18.0666 32.9333C18.0666 33.6888 18.311 34.311 18.7999 34.7999L26.3999 42.3999C26.9333 42.9333 27.5555 43.1999 28.2666 43.1999C28.9777 43.1999 29.5999 42.9333 30.1333 42.3999L45.1999 27.3333C45.6888 26.8444 45.9333 26.2221 45.9333 25.4666C45.9333 24.711 45.6888 24.0888 45.1999 23.5999C44.711 23.111 44.0888 22.8666 43.3333 22.8666C42.5777 22.8666 41.9555 23.111 41.4666 23.5999L28.2666 36.7999ZM31.9999 58.6666C28.311 58.6666 24.8444 57.9661 21.5999 56.5653C18.3555 55.1644 15.5333 53.2648 13.1333 50.8666C10.7333 48.4684 8.8337 45.6461 7.43459 42.3999C6.03548 39.1537 5.33503 35.687 5.33326 31.9999C5.33148 28.3128 6.03192 24.8461 7.43459 21.5999C8.83726 18.3537 10.7368 15.5315 13.1333 13.1333C15.5297 10.735 18.3519 8.83547 21.5999 7.43459C24.8479 6.0337 28.3146 5.33325 31.9999 5.33325C35.6853 5.33325 39.1519 6.0337 42.3999 7.43459C45.6479 8.83547 48.4701 10.735 50.8666 13.1333C53.263 15.5315 55.1635 18.3537 56.5679 21.5999C57.9724 24.8461 58.6719 28.3128 58.6666 31.9999C58.6613 35.687 57.9608 39.1537 56.5653 42.3999C55.1697 45.6461 53.2701 48.4684 50.8666 50.8666C48.463 53.2648 45.6408 55.1653 42.3999 56.5679C39.159 57.9706 35.6924 58.6701 31.9999 58.6666Z" fill="var(--accent)" />
    </svg>
);

const NoIcon = () => (
    <svg width="35" height="35" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle', marginBottom: '1px', marginRight: "2px" }}>
        <mask id="mask0_2252_2" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="5" y="5" width="54" height="54">
            <path d="M32 8C45.2533 8 56 18.7467 56 32C56 45.2533 45.2533 56 32 56C18.7467 56 8 45.2533 8 32C8 18.7467 18.7467 8 32 8Z" fill="white" stroke="white" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M31.9999 31.9999L42.6666 42.6666M31.9999 31.9999L21.3333 21.3333M31.9999 31.9999L21.3333 42.6666M31.9999 31.9999L42.6666 21.3333" stroke="black" strokeWidth="5.33333" strokeLinecap="round" strokeLinejoin="round" />
        </mask>
        <g mask="url(#mask0_2252_2)">
            <path d="M64 0H0V64H64V0Z" fill="var(--warning)" />
        </g>
    </svg>
);

export default Lesson
